import { EventEmitter } from '../events/EventEmitter';
import type {
  ConnectionStatus,
  ConnectionConfig,
  WebSocketLike,
  WebSocketFactory,
} from '../types/connection';
import { DEFAULT_CONNECTION_CONFIG } from '../types/connection';
import {
  ConnectionError,
  ConnectionTimeoutError,
  WebSocketError,
  NotConnectedError,
} from '../errors/BridgeError';
import type { Logger } from '../types/config';

/**
 * Connection manager events
 */
export interface ConnectionManagerEvents {
  /** Connection status changed */
  'status': (status: ConnectionStatus) => void;

  /** Connection error occurred */
  'error': (error: Error) => void;

  /** Message received from WebSocket */
  'message': (data: string) => void;

  /** Connection opened */
  'open': () => void;

  /** Connection closed */
  'close': (code: number, reason: string) => void;
}

/**
 * Connection manager options
 */
export interface ConnectionManagerOptions {
  /** Connection configuration */
  config?: Partial<ConnectionConfig>;

  /** Custom WebSocket factory */
  webSocketFactory?: WebSocketFactory;

  /** Logger instance */
  logger?: Logger;
}

/**
 * Manages WebSocket connections with auto-reconnect and heartbeat
 */
export class ConnectionManager extends EventEmitter<ConnectionManagerEvents> {
  private ws: WebSocketLike | null = null;
  private status: ConnectionStatus = 'disconnected';
  private reconnectAttempt = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;
  private connectPromise: Promise<void> | null = null;
  private connectResolve: (() => void) | null = null;
  private connectReject: ((error: Error) => void) | null = null;
  private lastPongTime = 0;
  private intentionalDisconnect = false;

  private readonly config: ConnectionConfig;
  private readonly webSocketFactory: WebSocketFactory;
  private readonly logger?: Logger;

  constructor(options: ConnectionManagerOptions = {}) {
    super();
    this.config = { ...DEFAULT_CONNECTION_CONFIG, ...options.config };
    this.webSocketFactory = options.webSocketFactory ?? this.getDefaultWebSocketFactory();
    this.logger = options.logger;
  }

  /**
   * Get current connection status
   */
  getStatus(): ConnectionStatus {
    return this.status;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.status === 'connected';
  }

  /**
   * Get the WebSocket URL
   */
  getUrl(): string {
    return this.config.url;
  }

  /**
   * Connect to the WebSocket server
   */
  async connect(): Promise<void> {
    // Already connected
    if (this.status === 'connected' && this.ws) {
      return;
    }

    // Connection in progress
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.intentionalDisconnect = false;
    this.reconnectAttempt = 0;

    return this.createConnection();
  }

  /**
   * Disconnect from the WebSocket server
   */
  async disconnect(): Promise<void> {
    this.intentionalDisconnect = true;
    this.clearReconnectTimeout();
    this.clearHeartbeat();

    if (this.ws) {
      this.setStatus('disconnecting');

      return new Promise<void>((resolve) => {
        const closeHandler = (code: number, reason: string) => {
          this.emit('close', code, reason);
          this.cleanup();
          this.setStatus('disconnected');
          resolve();
        };

        // Set a timeout in case close doesn't fire
        const timeoutId = setTimeout(() => {
          closeHandler(1000, 'Timeout');
        }, 1000);

        if (this.ws) {
          this.ws.onclose = (event) => {
            clearTimeout(timeoutId);
            const code = typeof event === 'object' ? event.code : 1000;
            const reason = typeof event === 'object' ? event.reason : '';
            closeHandler(code, reason);
          };
          this.ws.close(1000, 'Client disconnect');
        } else {
          clearTimeout(timeoutId);
          this.setStatus('disconnected');
          resolve();
        }
      });
    }

    this.setStatus('disconnected');
  }

  /**
   * Reconnect to the WebSocket server
   */
  async reconnect(): Promise<void> {
    await this.disconnect();
    this.intentionalDisconnect = false;
    return this.connect();
  }

  /**
   * Send a message through the WebSocket
   */
  send(data: string): void {
    if (!this.ws || this.status !== 'connected') {
      throw new NotConnectedError();
    }

    try {
      this.ws.send(data);
      this.logger?.debug('Sent message:', data);
    } catch (error) {
      throw new WebSocketError(
        'Failed to send message',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Update configuration (only when disconnected)
   */
  updateConfig(config: Partial<ConnectionConfig>): void {
    if (this.status !== 'disconnected') {
      throw new Error('Cannot update config while connected');
    }
    Object.assign(this.config, config);
  }

  /**
   * Create a new WebSocket connection
   */
  private createConnection(): Promise<void> {
    this.setStatus('connecting');

    this.connectPromise = new Promise<void>((resolve, reject) => {
      this.connectResolve = resolve;
      this.connectReject = reject;

      try {
        this.ws = this.webSocketFactory(this.config.url) as WebSocketLike;
        this.setupWebSocketHandlers();
        this.setupConnectionTimeout();
      } catch (error) {
        this.handleConnectionError(
          new ConnectionError(
            `Failed to create WebSocket: ${error instanceof Error ? error.message : String(error)}`
          )
        );
      }
    });

    return this.connectPromise;
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.logger?.info('WebSocket connected');
      this.reconnectAttempt = 0;
      this.setStatus('connected');
      this.startHeartbeat();
      this.emit('open');
      this.resolveConnect();
    };

    this.ws.onclose = (event) => {
      const code = typeof event === 'object' ? event.code : 1000;
      const reason = typeof event === 'object' ? event.reason : '';

      this.logger?.info(`WebSocket closed: ${code} ${reason}`);
      this.handleDisconnect(code, reason);
    };

    this.ws.onerror = (event) => {
      const message = typeof event === 'object' && 'message' in event
        ? (event as { message: string }).message
        : 'WebSocket error';

      this.logger?.error('WebSocket error:', message);
      const error = new WebSocketError(message);
      this.emit('error', error);
    };

    this.ws.onmessage = (event) => {
      const data = typeof event === 'object' ? event.data : event;
      if (typeof data === 'string') {
        this.handleMessage(data);
      }
    };
  }

  /**
   * Setup connection timeout
   */
  private setupConnectionTimeout(): void {
    setTimeout(() => {
      if (this.status === 'connecting') {
        this.handleConnectionError(
          new ConnectionTimeoutError(this.config.connectTimeoutMs)
        );
      }
    }, this.config.connectTimeoutMs);
  }

  /**
   * Handle incoming message
   */
  private handleMessage(data: string): void {
    this.logger?.debug('Received message:', data);

    // Handle pong for heartbeat
    try {
      const parsed = JSON.parse(data);
      if (parsed.type === 'pong') {
        this.lastPongTime = Date.now();
        return;
      }
    } catch {
      // Not JSON, pass through
    }

    this.emit('message', data);
  }

  /**
   * Handle disconnect event
   */
  private handleDisconnect(code: number, reason: string): void {
    this.clearHeartbeat();
    this.emit('close', code, reason);

    if (this.intentionalDisconnect) {
      this.setStatus('disconnected');
      this.cleanup();
      return;
    }

    // Attempt reconnection if enabled
    if (this.config.autoReconnect && this.reconnectAttempt < this.config.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      this.setStatus('disconnected');
      this.cleanup();

      if (this.reconnectAttempt >= this.config.maxReconnectAttempts) {
        this.emit('error', new ConnectionError('Max reconnection attempts reached'));
      }
    }
  }

  /**
   * Handle connection error
   */
  private handleConnectionError(error: Error): void {
    this.logger?.error('Connection error:', error.message);
    this.emit('error', error);

    if (this.connectReject) {
      this.connectReject(error);
      this.cleanup();
    }

    // Try reconnecting
    if (this.config.autoReconnect && !this.intentionalDisconnect) {
      this.scheduleReconnect();
    } else {
      this.setStatus('disconnected');
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    this.setStatus('reconnecting');
    this.reconnectAttempt++;

    const delay = Math.min(
      this.config.reconnectBaseDelayMs * Math.pow(2, this.reconnectAttempt - 1),
      30000 // Max 30 seconds
    );

    this.logger?.info(
      `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt}/${this.config.maxReconnectAttempts})`
    );

    this.reconnectTimeoutId = setTimeout(() => {
      this.createConnection().catch((error) => {
        this.logger?.error('Reconnection failed:', error.message);
      });
    }, delay);
  }

  /**
   * Start heartbeat mechanism
   */
  private startHeartbeat(): void {
    if (this.config.heartbeatIntervalMs <= 0) return;

    this.lastPongTime = Date.now();

    this.heartbeatIntervalId = setInterval(() => {
      if (!this.ws || this.status !== 'connected') {
        this.clearHeartbeat();
        return;
      }

      // Check if we've received a pong recently
      const timeSinceLastPong = Date.now() - this.lastPongTime;
      if (timeSinceLastPong > this.config.heartbeatIntervalMs * 2) {
        this.logger?.warn('Heartbeat timeout, connection may be dead');
        this.ws.close(4000, 'Heartbeat timeout');
        return;
      }

      // Send ping
      try {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      } catch (error) {
        this.logger?.error('Failed to send heartbeat:', error);
      }
    }, this.config.heartbeatIntervalMs);
  }

  /**
   * Clear heartbeat interval
   */
  private clearHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
  }

  /**
   * Clear reconnect timeout
   */
  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  /**
   * Resolve the connect promise
   */
  private resolveConnect(): void {
    if (this.connectResolve) {
      this.connectResolve();
      this.connectPromise = null;
      this.connectResolve = null;
      this.connectReject = null;
    }
  }

  /**
   * Cleanup after disconnect
   */
  private cleanup(): void {
    this.ws = null;
    this.connectPromise = null;
    this.connectResolve = null;
    this.connectReject = null;
  }

  /**
   * Set connection status and emit event
   */
  private setStatus(status: ConnectionStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.emit('status', status);
    }
  }

  /**
   * Get the default WebSocket factory
   */
  private getDefaultWebSocketFactory(): WebSocketFactory {
    return (url: string, protocols?: string | string[]): WebSocketLike => {
      // Browser environment
      if (typeof WebSocket !== 'undefined') {
        return new WebSocket(url, protocols) as unknown as WebSocketLike;
      }

      // Node.js environment - try to use 'ws' package
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const WS = require('ws');
        return new WS(url, protocols) as WebSocketLike;
      } catch {
        throw new Error(
          'WebSocket is not available. In Node.js, install the "ws" package: npm install ws'
        );
      }
    };
  }
}

/**
 * Create a new ConnectionManager instance
 */
export function createConnectionManager(
  options?: ConnectionManagerOptions
): ConnectionManager {
  return new ConnectionManager(options);
}
